/**
 * "Match all reports" (2026-10-09, Litsey Creek #092678280; mockup
 * threads/report-match-all/mockup.html).
 *
 * One look, one button for every plain PDF on the job that should be a
 * device's official report. The server reads each file's name and text and
 * sorts it into buckets; the office sees the buckets as big colored counts,
 * un-checks anything that looks wrong, and files the rest at once:
 *
 *   ✓ green   files to a device already on the job
 *   + blue    the device is at this site but not on the job — add it, then file
 *   = grey    that device already has this exact file (nothing to do)
 *   ? amber   names two devices — tap the right one
 *   – grey    no device serial found
 *
 * Also opened straight after an upload whose files didn't all file themselves:
 * the old one-card "Which device?" question could only hold one file, so a
 * 31-file drop lost every question but the last.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, CircleHelp, Copy, Loader2, MinusCircle, PlusCircle } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export type ReportMatchAction = "file" | "add_and_file" | "already_filed" | "duplicate" | "ambiguous" | "no_match";

export interface ReportMatchDevice {
    assetId: string;
    serial: string | null;
    label: string;
    size: string | null;
    assemblyType: string | null;
    make: string | null;
    location: string | null;
    onJob: boolean;
}

export interface ReportMatchRow {
    attachmentId: string;
    fileName: string;
    action: ReportMatchAction;
    device: ReportMatchDevice | null;
    candidates: ReportMatchDevice[];
    serialsRead: string[];
    replaces: { attachmentId: string; fileName: string | null } | null;
}

interface Preview {
    rows: ReportMatchRow[];
    counts: Record<ReportMatchAction, number>;
}

interface ApplyResult {
    results: Array<{
        attachmentId: string;
        ok: boolean;
        serial: string | null;
        linkedToJob: boolean;
        error: string | null;
    }>;
    filed: number;
    linked: number;
    failed: number;
}

/** Rows that start checked: the two that file. Everything else needs a tap. */
export function defaultChecked(rows: ReportMatchRow[]): Map<string, string> {
    const out = new Map<string, string>();
    for (const r of rows) {
        if ((r.action === "file" || r.action === "add_and_file") && r.device) out.set(r.attachmentId, r.device.assetId);
    }
    return out;
}

/** The plain-English toast after an apply. Never claims a filing that failed. */
export function applyToastText(r: Pick<ApplyResult, "filed" | "linked" | "failed">): { text: string; failed: boolean } {
    const parts: string[] = [];
    if (r.filed > 0) parts.push(`Filed ${r.filed} report${r.filed === 1 ? "" : "s"}`);
    if (r.linked > 0) parts.push(`added ${r.linked} device${r.linked === 1 ? "" : "s"} to the job`);
    if (r.failed > 0) parts.push(`${r.failed} didn't file — still on the job as documents`);
    return { text: parts.join(" · ") || "Nothing filed.", failed: r.failed > 0 };
}

const BUCKETS: Array<{
    action: ReportMatchAction;
    label: string;
    icon: typeof CheckCircle2;
    tone: string;
}> = [
    {
        action: "file",
        label: "File",
        icon: CheckCircle2,
        tone: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
    },
    {
        action: "add_and_file",
        label: "Add to job + file",
        icon: PlusCircle,
        tone: "border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300",
    },
    {
        action: "ambiguous",
        label: "Pick one",
        icon: CircleHelp,
        tone: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    },
    {
        action: "already_filed",
        label: "Already filed",
        icon: Copy,
        tone: "border-border bg-muted/50 text-muted-foreground",
    },
    { action: "duplicate", label: "Second copy", icon: Copy, tone: "border-border bg-muted/50 text-muted-foreground" },
    {
        action: "no_match",
        label: "No serial",
        icon: MinusCircle,
        tone: "border-border bg-muted/50 text-muted-foreground",
    },
];

export function useReportMatchAll(jobId: string) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState<Map<string, string>>(new Map());

    const preview = useQuery({
        queryKey: ["report-match", jobId],
        enabled: open && !!jobId,
        staleTime: 0,
        queryFn: () => api.get<Preview>(`/api/attachments/job/${jobId}/report-match`),
    });
    // Fresh preview → fresh default ticks (the two buckets that file).
    useEffect(() => {
        if (preview.data) setChecked(defaultChecked(preview.data.rows));
    }, [preview.data]);

    const apply = useMutation({
        mutationFn: () =>
            api.post<ApplyResult>(`/api/attachments/job/${jobId}/report-match`, {
                items: [...checked.entries()].map(([attachmentId, assetId]) => ({ attachmentId, assetId })),
            }),
        onSuccess: (r) => {
            const t = applyToastText(r);
            if (t.failed) toast.error(t.text, { duration: 12_000 });
            else toast.success(t.text);
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["close-out-queue"] });
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            if (r.failed === 0) setOpen(false);
            else void preview.refetch();
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't file those reports.");
        },
    });

    const element = (
        <ReportMatchAllDialog
            open={open}
            loading={preview.isFetching}
            error={preview.isError}
            data={preview.data ?? null}
            checked={checked}
            setChecked={setChecked}
            busy={apply.isPending}
            onClose={() => setOpen(false)}
            onApply={() => apply.mutate()}
        />
    );
    return {
        open: () => {
            setOpen(true);
            void qc.invalidateQueries({ queryKey: ["report-match", jobId] });
        },
        element,
    };
}

function DeviceChip({
    d,
    selected,
    onClick,
    disabled,
}: {
    d: ReportMatchDevice;
    selected?: boolean;
    onClick?: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            disabled={disabled || !onClick}
            onClick={onClick}
            aria-pressed={selected}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border-2 px-2 py-1 text-left text-xs",
                selected ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/40" : "border-border bg-card",
                onClick && !disabled && "hover:border-primary",
            )}
        >
            <span className="font-extrabold tabular-nums">{d.serial ?? "—"}</span>
            <span className="text-muted-foreground">
                {[d.size, d.assemblyType?.toUpperCase()].filter(Boolean).join(" ")}
            </span>
            {!d.onJob && (
                <span className="rounded bg-sky-100 px-1 text-[10px] font-bold text-sky-800 dark:bg-sky-900/50 dark:text-sky-300">
                    + add
                </span>
            )}
        </button>
    );
}

function ReportMatchAllDialog({
    open,
    loading,
    error,
    data,
    checked,
    setChecked,
    busy,
    onClose,
    onApply,
}: {
    open: boolean;
    loading: boolean;
    error: boolean;
    data: Preview | null;
    checked: Map<string, string>;
    setChecked: (m: Map<string, string>) => void;
    busy: boolean;
    onClose: () => void;
    onApply: () => void;
}) {
    const rows = useMemo(() => data?.rows ?? [], [data]);
    const toggle = (attachmentId: string, assetId: string | null) => {
        const next = new Map(checked);
        if (!assetId || next.get(attachmentId) === assetId) next.delete(attachmentId);
        else next.set(attachmentId, assetId);
        setChecked(next);
    };
    const n = checked.size;
    const adds = rows.filter((r) => {
        const pick = checked.get(r.attachmentId);
        if (!pick) return false;
        const d = r.device?.assetId === pick ? r.device : r.candidates.find((c) => c.assetId === pick);
        return d ? !d.onJob : false;
    }).length;

    return (
        <Dialog open={open} onClose={onClose} title="Match reports to devices" className="max-w-3xl">
            <div className="flex flex-col gap-4" data-testid="report-match-all">
                {loading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Reading every file on the job…
                    </div>
                )}
                {error && <p className="text-sm text-red-700">Couldn&apos;t read the files. Try again.</p>}
                {!loading && data && rows.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                        No loose PDFs on this job — every report is already on its device.
                    </p>
                )}
                {!loading && data && rows.length > 0 && (
                    <>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                            {BUCKETS.map((b) => {
                                const Icon = b.icon;
                                return (
                                    <div
                                        key={b.action}
                                        className={cn("flex flex-col items-center rounded-xl border p-2", b.tone)}
                                    >
                                        <Icon className="h-5 w-5" />
                                        <span className="text-2xl font-extrabold leading-tight tabular-nums">
                                            {data.counts[b.action]}
                                        </span>
                                        <span className="text-center text-[11px] font-semibold leading-tight">
                                            {b.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <ul className="divide-y divide-border rounded-xl border border-border">
                            {rows.map((r) => {
                                const b = BUCKETS.find((x) => x.action === r.action)!;
                                const Icon = b.icon;
                                const pick = checked.get(r.attachmentId) ?? null;
                                const actionable =
                                    (r.action === "file" || r.action === "add_and_file") && r.device !== null;
                                return (
                                    <li key={r.attachmentId} className="flex flex-wrap items-center gap-2 px-3 py-2">
                                        {actionable ? (
                                            <input
                                                type="checkbox"
                                                className="h-5 w-5 accent-emerald-700"
                                                checked={pick === r.device!.assetId}
                                                onChange={() => toggle(r.attachmentId, r.device!.assetId)}
                                                aria-label={`File ${r.fileName}`}
                                            />
                                        ) : (
                                            <span className="w-5" />
                                        )}
                                        <span
                                            className={cn(
                                                "grid h-7 w-7 shrink-0 place-items-center rounded-full border",
                                                b.tone,
                                            )}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-sm" title={r.fileName}>
                                            {r.fileName}
                                        </span>
                                        {r.device && (
                                            <DeviceChip
                                                d={r.device}
                                                selected={actionable && pick === r.device.assetId}
                                            />
                                        )}
                                        {r.action === "ambiguous" &&
                                            r.candidates.map((c) => (
                                                <DeviceChip
                                                    key={c.assetId}
                                                    d={c}
                                                    selected={pick === c.assetId}
                                                    onClick={() => toggle(r.attachmentId, c.assetId)}
                                                />
                                            ))}
                                        {r.action === "no_match" && (
                                            <span className="text-xs text-muted-foreground">
                                                {r.serialsRead.length > 0
                                                    ? `Says S/N ${r.serialsRead.join(", ")} — not a device here`
                                                    : "No serial found"}
                                            </span>
                                        )}
                                        {r.replaces && pick && (
                                            <span className="basis-full pl-14 text-[11px] text-amber-800 dark:text-amber-300">
                                                Replaces the report already on this device
                                                {r.replaces.fileName ? ` (${r.replaces.fileName})` : ""}.
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
                <div className="flex flex-wrap items-center gap-2">
                    <Button disabled={n === 0 || busy || loading} onClick={onApply}>
                        {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                        File {n} report{n === 1 ? "" : "s"}
                        {adds > 0 ? ` · add ${adds} device${adds === 1 ? "" : "s"}` : ""}
                    </Button>
                    <Button variant="outline" onClick={onClose} disabled={busy}>
                        Not now
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
